import type { Camelized } from '@skytech/utils'
import { mapKeysToCamelCase, stringToSnakeCase, toCamelCase } from '@skytech/utils'
import saveAs from 'file-saver'

import { appendValidatedParam } from '@/util/append-param'
import { get } from '@/util/auth'
import { WAIVER_URL_NEW } from '@/util/misc'

import type {
  TerminationOrderOriginal,
  OrderStatusOriginal,
  PortingOrderOriginal,
  UpgradeOrderOriginal,
} from './types'

import {
  API_ROUTE_PORTING_SUBSCRIPTION_ORDERS,
  API_ROUTE_PORTING_SUBSCRIPTION_TOTAL_ORDERS,
  API_ROUTE_SUBSCRIPTION_ORDERS_META_DATA,
  API_ROUTE_TERMINATION_SUBSCRIPTION_ORDERS,
  API_ROUTE_UPGRADE_SUBSCRIPTION_ORDERS,
} from '../../api/apiRoutes'

const subscriptionOrderResources = {
  getPortingList: async ({
    offset,
    limit,
    keyword,
    status,
    sortBy,
    sort,
  }: {
    offset: number
    limit: number
    status: string
    keyword: string
    sortBy?: string
    sort?: string
  }) => {
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    })
    appendValidatedParam({
      params,
      paramName: 'keyword',
      value: keyword,
    })

    appendValidatedParam({
      params,
      paramName: 'status',
      value: status,
    })

    if (sortBy && sort) {
      const sortBySnakeCase = sortBy ? stringToSnakeCase(sortBy) : undefined
      sortBySnakeCase &&
        appendValidatedParam({
          params,
          paramName: 'sortBy',
          value: sortBySnakeCase,
        })

      appendValidatedParam({
        params,
        paramName: 'sort',
        value: sort,
      })
    }

    const orders = await get<PortingOrderOriginal[]>(
      `${API_ROUTE_PORTING_SUBSCRIPTION_ORDERS}?${params.toString()}`
    )
    return mapKeysToCamelCase(orders).map(order => ({
      ...order,
      subStatus: toCamelCase(order.subStatus),
    }))
  },
  getTerminationList: async ({
    offset,
    limit,
    keyword,
    status,
    terminationType,
    sortBy,
    sort,
  }: {
    offset: number
    limit: number
    status: string
    keyword: string
    terminationType?: string
    sortBy?: string
    sort?: string
  }) => {
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    })
    appendValidatedParam({
      params,
      paramName: 'keyword',
      value: keyword,
    })

    appendValidatedParam({
      params,
      paramName: 'status',
      value: status,
    })

    terminationType &&
      appendValidatedParam({
        params,
        paramName: 'terminationType',
        value: terminationType,
      })

    if (sortBy && sort) {
      const sortBySnakeCase = sortBy ? stringToSnakeCase(sortBy) : undefined
      sortBySnakeCase &&
        appendValidatedParam({
          params,
          paramName: 'sortBy',
          value: sortBySnakeCase,
        })

      appendValidatedParam({
        params,
        paramName: 'sort',
        value: sort,
      })
    }

    const orders = await get<TerminationOrderOriginal[]>(
      `${API_ROUTE_TERMINATION_SUBSCRIPTION_ORDERS}?${params.toString()}`
    )
    return mapKeysToCamelCase(orders).map(order => ({
      ...order,
      subStatus: toCamelCase(order.subStatus),
    }))
  },
  getUpgradeList: async ({
    offset,
    limit,
    keyword,
    status,
    sortBy,
    sort,
  }: {
    offset: number
    limit: number
    status: string
    keyword: string
    sortBy?: string
    sort?: string
  }) => {
    const params = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString(),
    })
    appendValidatedParam({
      params,
      paramName: 'keyword',
      value: keyword,
    })

    appendValidatedParam({
      params,
      paramName: 'status',
      value: status,
    })

    if (sortBy && sort) {
      const sortBySnakeCase = sortBy ? stringToSnakeCase(sortBy) : undefined
      sortBySnakeCase &&
        appendValidatedParam({
          params,
          paramName: 'sortBy',
          value: sortBySnakeCase,
        })

      appendValidatedParam({
        params,
        paramName: 'sort',
        value: sort,
      })
    }

    const orders = await get<UpgradeOrderOriginal[]>(
      `${API_ROUTE_UPGRADE_SUBSCRIPTION_ORDERS}?${params.toString()}`
    )
    return mapKeysToCamelCase(orders).map(order => ({
      ...order,
      subStatus: toCamelCase(order.subStatus),
    }))
  },
  getTotalOrders: async ({
    orderType,
    keyword,
    status,
    terminationType,
  }: {
    orderType: 'onboarding' | 'termination' | 'upgrade'
    status: string
    keyword?: string
    terminationType?: string
  }) => {
    const params = new URLSearchParams()
    keyword &&
      appendValidatedParam({
        params,
        paramName: 'keyword',
        value: keyword,
      })

    appendValidatedParam({
      params,
      paramName: 'status',
      value: status,
    })

    terminationType &&
      appendValidatedParam({
        params,
        paramName: 'terminationType',
        value: terminationType,
      })
    const total = await get<number>(
      `${API_ROUTE_PORTING_SUBSCRIPTION_TOTAL_ORDERS.replace(':order_type', orderType)}?${params.toString()}`
    )
    return total
  },
  getWaiver: async (code: string) => {
    const response = await get<any>(WAIVER_URL_NEW.replace(':code', code))
    saveAs(atob(response.body), response.filename)
    return { filename: response.filename }
  },
  getOrdersMetaData: async () => {
    type SubscriptionOrderCounterKeys =
      | Camelized<Exclude<OrderStatusOriginal, 'DONE'>>
      | 'completed'
    type SubscriptionOrderCounter = Record<SubscriptionOrderCounterKeys, number>
    const desiredOrder: (keyof SubscriptionOrderCounter)[] = [
      'forManualHandling',
      'open',
      'all',
      'waitingForUser',
      'waitingForDealer',
      'processing',
      'terminated',
      'terminationRequest',
      'completed',
      'canceled',
      'failed',
    ]
    const counter = await get<{
      onboardingCount: SubscriptionOrderCounter
      terminationCount: SubscriptionOrderCounter
      upgradeCount: SubscriptionOrderCounter
    }>(API_ROUTE_SUBSCRIPTION_ORDERS_META_DATA)
    function reorderStatusCounter<T extends Record<(typeof desiredOrder)[number], number>>(
      response: T
    ): T {
      return Object.fromEntries(desiredOrder.map(key => [key, response[key]])) as T
    }

    return {
      onboardingCount: reorderStatusCounter(counter.onboardingCount),
      terminationCount: reorderStatusCounter(counter.terminationCount),
      upgradeCount: reorderStatusCounter(counter.upgradeCount),
    }
  },
}

export default subscriptionOrderResources

export type PortingOrder = Awaited<
  ReturnType<typeof subscriptionOrderResources.getPortingList>
>[number]
export type TerminationOrder = Awaited<
  ReturnType<typeof subscriptionOrderResources.getTerminationList>
>[number]
export type UpgradeOrder = Awaited<
  ReturnType<typeof subscriptionOrderResources.getUpgradeList>
>[number]
export type SubOrder = PortingOrder | TerminationOrder | UpgradeOrder
export type OrderStatus = UpgradeOrder['subStatus']
export type OrderMetaData = Awaited<ReturnType<typeof subscriptionOrderResources.getOrdersMetaData>>
export type OrderStatusCount = OrderMetaData['onboardingCount']
